import {
  ActionIcon,
  Badge,
  Button,
  Collapse,
  Group,
  Stack,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconChevronDown, IconChevronUp, IconLanguage, IconTrash } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import {
  LOCALE_LABELS,
  NON_DEFAULT_LOCALES,
  type LocalizedString,
  type SupportedLocale,
  hasAnyTranslation,
  setDefault,
  setTranslation,
} from '../api/localized-string';

/**
 * Reusable input for an authored translatable string. Renders the
 * default (English) input always-on, and a collapsible "Translations"
 * section listing one row per non-English supported locale. Locales
 * without a translation yet show a "+ Add fr" button; clicking it
 * expands an input. The trash icon empties a translation.
 *
 * Compact mode (used inside option-label tables) skips the section
 * label and shrinks paddings; the disclosure pattern stays the same.
 */
export interface LocalizedStringInputProps {
  /** Label for the default (English) input. */
  label?: string;
  description?: string;
  value: LocalizedString;
  onChange: (next: LocalizedString) => void;
  /** Multiline default input (description-style fields). */
  multiline?: boolean;
  disabled?: boolean;
  /** Slim variant for inline use inside option-label rows. */
  compact?: boolean;
  required?: boolean;
  placeholder?: string;
}

export function LocalizedStringInput(props: LocalizedStringInputProps) {
  const {
    value,
    onChange,
    multiline,
    disabled,
    compact,
    label,
    description,
    required,
    placeholder,
  } = props;
  // Open the translations section automatically when the row already
  // has translations so an editor opening an existing row sees the
  // variants without an extra click.
  const [opened, { toggle }] = useDisclosure(hasAnyTranslation(value));
  const count = value.translations ? Object.keys(value.translations).length : 0;
  // Track locales the operator has explicitly added even when their
  // current translation is empty. Without this, typing-then-clearing
  // a translation would unmount the input mid-edit (because the
  // empty-text branch of setTranslation deletes the key).
  const [shownLocales, setShownLocales] = useState<Set<SupportedLocale>>(
    () => new Set(value.translations ? (Object.keys(value.translations) as SupportedLocale[]) : []),
  );
  // Keep the shown set in sync when the value prop changes from
  // outside (e.g. parent reloads from API). New locale keys flow in;
  // explicitly-added-but-empty rows the operator opened stay open.
  useEffect(() => {
    const present = new Set(
      value.translations ? (Object.keys(value.translations) as SupportedLocale[]) : [],
    );
    setShownLocales((prev) => new Set([...prev, ...present]));
  }, [value.translations]);

  function addLocaleRow(locale: SupportedLocale) {
    setShownLocales((prev) => new Set(prev).add(locale));
  }

  function removeLocaleRow(locale: SupportedLocale) {
    setShownLocales((prev) => {
      const next = new Set(prev);
      next.delete(locale);
      return next;
    });
    onChange(setTranslation(value, locale, ''));
  }

  const DefaultInput = multiline ? Textarea : TextInput;

  return (
    <Stack gap={compact ? 4 : 6}>
      <DefaultInput
        label={label}
        description={description}
        value={value.default}
        onChange={(e) => onChange(setDefault(value, e.currentTarget.value))}
        disabled={disabled}
        required={required}
        placeholder={placeholder}
        autosize={multiline ? true : undefined}
        minRows={multiline ? 2 : undefined}
        size={compact ? 'xs' : 'sm'}
      />
      <Group gap="xs">
        <Button
          variant="subtle"
          size="compact-xs"
          color="gray"
          leftSection={<IconLanguage size={12} />}
          rightSection={opened ? <IconChevronUp size={12} /> : <IconChevronDown size={12} />}
          onClick={toggle}
        >
          Translations
          {count > 0 && (
            <Badge ml={6} size="xs" variant="filled" color="blue" radius="sm">
              {count}
            </Badge>
          )}
        </Button>
      </Group>
      <Collapse in={opened}>
        <Stack
          gap={compact ? 4 : 6}
          pl="md"
          py={compact ? 4 : 6}
          style={{
            borderLeft: '2px solid var(--mantine-color-gray-3)',
          }}
        >
          {NON_DEFAULT_LOCALES.map((loc) => (
            <LocaleRow
              key={loc}
              locale={loc}
              value={value}
              onChange={onChange}
              multiline={multiline}
              disabled={disabled}
              compact={compact}
              shown={shownLocales.has(loc)}
              onAdd={() => addLocaleRow(loc)}
              onRemove={() => removeLocaleRow(loc)}
            />
          ))}
        </Stack>
      </Collapse>
    </Stack>
  );
}

function LocaleRow({
  locale,
  value,
  onChange,
  multiline,
  disabled,
  compact,
  shown,
  onAdd,
  onRemove,
}: {
  locale: Exclude<SupportedLocale, 'en'>;
  value: LocalizedString;
  onChange: (next: LocalizedString) => void;
  multiline?: boolean;
  disabled?: boolean;
  compact?: boolean;
  shown: boolean;
  onAdd: () => void;
  onRemove: () => void;
}) {
  const text = value.translations?.[locale] ?? '';

  if (!shown) {
    return (
      <Group justify="space-between" gap="xs">
        <Text size="xs" c="dimmed" w={120}>
          {LOCALE_LABELS[locale]}{' '}
          <Text component="span" c="gray.5">
            ({locale})
          </Text>
        </Text>
        <Button variant="subtle" size="compact-xs" color="gray" disabled={disabled} onClick={onAdd}>
          + Add {locale}
        </Button>
      </Group>
    );
  }

  const Input = multiline ? Textarea : TextInput;
  return (
    <Group gap="xs" align="flex-start" wrap="nowrap">
      <Text size="xs" c="dimmed" w={120} mt={6} style={{ flexShrink: 0 }}>
        {LOCALE_LABELS[locale]}{' '}
        <Text component="span" c="gray.5">
          ({locale})
        </Text>
      </Text>
      <Input
        value={text}
        onChange={(e) => onChange(setTranslation(value, locale, e.currentTarget.value))}
        disabled={disabled}
        autosize={multiline ? true : undefined}
        minRows={multiline ? 2 : undefined}
        size={compact ? 'xs' : 'sm'}
        style={{ flex: 1 }}
      />
      <ActionIcon
        variant="subtle"
        color="red"
        size={compact ? 'sm' : 'md'}
        onClick={onRemove}
        aria-label={`Remove ${locale} translation`}
        disabled={disabled}
      >
        <IconTrash size={compact ? 12 : 14} />
      </ActionIcon>
    </Group>
  );
}
